import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, BookOpen, Heart, MessageCircle, Users, Plus, X,
  Calendar, Bell, Trophy, Home, Shield, FileText, LogOut,
  Settings, Award, Share2, HelpCircle, Building2, Lightbulb,
  LayoutDashboard
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Browser } from '@capacitor/browser';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const BottomNav = ({ active }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const [showMenu, setShowMenu] = useState(false);

  const navItems = [
    { id: 'home', icon: Home, label: 'Accueil', path: '/' },
    { id: 'dashboard', icon: LayoutDashboard, label: 'Tableau de bord', path: '/dashboard' },
    { id: 'modules', icon: BookOpen, label: 'Modules', path: '/modules' },
    { id: 'calendar', icon: Calendar, label: 'Calendrier', path: '/calendar' },
    { id: 'more', icon: Plus, label: 'Plus', path: null }
  ];

  const menuItems = [
    { icon: User, label: 'Mon Profil', path: '/profile', color: 'text-pink-300' },
    { icon: MessageCircle, label: 'Communauté', path: '/community', color: 'text-indigo-300' },
    { icon: Users, label: 'Girls Room', path: '/girls-room', color: 'text-fuchsia-300', badge: 'Populaire' },
    { icon: Heart, label: 'Santé', path: '/health', color: 'text-red-300' },
    { icon: Lightbulb, label: 'Trucs & Astuces', path: '/tips', color: 'text-yellow-300', badge: 'Nouveau' },
    { icon: Bell, label: 'Notifications', path: '/notifications', color: 'text-orange-300', notifBadge: true },
    { icon: Trophy, label: 'Classement', path: '/leaderboard', color: 'text-amber-300' },
    { icon: Award, label: 'Mes Badges', path: '/profile', color: 'text-purple-300' },
    { divider: true },
    { icon: Building2, label: 'Partenaires', path: '/partners', color: 'text-blue-300' },
    { icon: Share2, label: 'Partager l\'app', action: 'share', color: 'text-green-300', badge: '+5 pts' },
    { divider: true },
    { icon: FileText, label: 'Conditions d\'utilisation', path: '/terms', color: 'text-gray-300' },
    { icon: Shield, label: 'Confidentialité', path: '/privacy', color: 'text-gray-300' },
    { icon: Shield, label: 'Sécurité', path: '/security', color: 'text-gray-300' },
  ];

  // Add admin link if user is super_admin
  if (user?.role === 'super_admin') {
    menuItems.splice(6, 0, { 
      icon: Settings, 
      label: 'Administration', 
      path: '/admin', 
      color: 'text-orange-300',
      badge: 'Admin'
    });
  }

  const handleMenuClick = async (item) => {
    if (item.action === 'share') {
      const shareText = "Découvre Fleur de Vie, l'app gratuite pour comprendre ton corps! 🌸";
      const shareUrl = "https://fleurde-vie.com";
      const isNative = Capacitor.isNativePlatform();
      
      try {
        let shared = false;
        
        if (isNative) {
          // Use Capacitor Share plugin for native
          await Share.share({ 
            title: 'Fleur de Vie', 
            text: shareText, 
            url: shareUrl,
            dialogTitle: 'Partager Fleur de Vie'
          });
          shared = true;
        } else if (navigator.share) {
          // Use Web Share API
          await navigator.share({ title: 'Fleur de Vie', text: shareText, url: shareUrl });
          shared = true;
        } else {
          // Fallback to WhatsApp
          window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
          shared = true;
        }
        
        // Record share and award points
        if (shared) {
          try {
            const response = await api.post('/share/reward', { platform: 'menu' });
            if (response.data.pointsAwarded) {
              toast({
                title: '🎉 +5 points!',
                description: response.data.message || 'Merci pour le partage!',
              });
            }
            if (response.data.badgeEarned) {
              toast({
                title: '🏆 Nouveau Badge!',
                description: `Tu as débloqué "${response.data.badgeName}"!`,
              });
            }
          } catch (err) {
            console.log('Share recorded');
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share failed:', error);
          // Fallback: open WhatsApp
          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
          if (isNative) {
            await Browser.open({ url: whatsappUrl });
          } else {
            window.open(whatsappUrl, '_blank');
          }
        }
      }
    } else if (item.path) {
      navigate(item.path);
    }
    setShowMenu(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowMenu(false);
  };

  return (
    <>
      {/* Menu Overlay */}
      {showMenu && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowMenu(false)}
          />
          
          {/* Menu Panel */}
          <div className="absolute bottom-20 left-4 right-4 max-w-md mx-auto bg-purple-900/95 backdrop-blur-md rounded-2xl border border-white/20 shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h3 className="text-white font-bold text-lg">Menu</h3>
              <button 
                onClick={() => setShowMenu(false)}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-pink-300" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="max-h-[60vh] overflow-y-auto py-2">
              {menuItems.map((item, index) => {
                if (item.divider) {
                  return <div key={`divider-${index}`} className="h-px bg-white/10 my-2 mx-4" />;
                }
                return (
                  <button
                    key={item.path || item.action || item.label}
                    onClick={() => handleMenuClick(item)}
                    className="w-full flex items-center gap-4 px-5 py-3 hover:bg-white/10 transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-full bg-white/10 flex items-center justify-center ${item.color} relative`}>
                      <item.icon className="w-5 h-5" />
                      {item.notifBadge && unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notif-menu-badge">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </div>
                    <span className="text-white font-medium flex-1 text-left">{item.label}</span>
                    {item.badge && (
                      <span className="px-2 py-0.5 bg-orange-500/20 text-orange-300 text-xs rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}

              {/* Logout */}
              <div className="h-px bg-white/10 my-2 mx-4" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-4 px-5 py-3 hover:bg-red-500/10 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400">
                  <LogOut className="w-5 h-5" />
                </div>
                <span className="text-red-400 font-medium">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-purple-900/95 backdrop-blur-md border-t border-white/10 px-2 py-2 z-40">
        <div className="flex justify-around items-center max-w-md mx-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.id;
            const isMoreButton = item.id === 'more';
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (isMoreButton) {
                    setShowMenu(!showMenu);
                  } else {
                    navigate(item.path);
                  }
                }}
                className={`flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all ${
                  isMoreButton
                    ? 'text-white'
                    : isActive 
                      ? 'text-pink-300' 
                      : 'text-pink-200/60 hover:text-pink-200'
                }`}
              >
                {isMoreButton ? (
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    showMenu 
                      ? 'bg-pink-500 rotate-45' 
                      : 'bg-gradient-to-br from-pink-500 to-purple-600'
                  }`}>
                    <Plus className="w-6 h-6 text-white" />
                  </div>
                ) : (
                  <>
                    <Icon className={`w-6 h-6 mb-1 ${isActive ? 'text-pink-300' : ''}`} />
                    <span className="text-xs font-medium">{item.label}</span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default BottomNav;
